/* =========================================================
   Affiche ÉQUITATION « Mon cheval » — l'identité du cheval.
   API : SNEqCheval.render(data, opts) -> string SVG
     data : { mode, titre, kicker, cavalier, club, depuis,
              mode 'fiche'       → l1..l8 (« Libellé|valeur »)
              mode 'nom'         → nom, surnom, ligne1, ligne2
              mode 'caractere'   → t1..t6 (« Calme|Vif|72 »), resume
              mode 'signalement' → marqueTete, ag/ad/pg/pd (hauteur de balzane 0-100)
              mode 'robe'        → robe (clé de ROBES)
              + s1..s3 (« valeur|libellé ») pour le bandeau du bas }
     opts : { palette }

   Un cavalier n'aime pas « l'équitation », il aime SON cheval : le nom est
   donc toujours l'élément le plus gros de l'affiche.

   Auto-suffisant (aucun fetch, aucune image) → re-render serveur direct.
   ========================================================= */
(function () {
  const PW = 800, PH = 1066, MX = 64;

  const PALETTES = {
    craie:    { label:'Craie',    bg:'#F7F4ED', ink:'#0B2D5B', muted:'#8B94A3', accent:'#FF6B2C', line:'#E3DDD0', card:'#FFFFFF', sable:'#E4D5B7', deep:'#D8C7A4' },
    sable:    { label:'Sable',    bg:'#EDE3D2', ink:'#2C3A2E', muted:'#8A8676', accent:'#C4622D', line:'#DDD0B9', card:'#F7F1E6', sable:'#DCCFB6', deep:'#CDBE9E' },
    carriere: { label:'Carrière', bg:'#12362B', ink:'#F4F1E8', muted:'#8FB3A5', accent:'#FF6B2C', line:'#1C5544', card:'#164536', sable:'#C9B489', deep:'#0B271F' },
    nuit:     { label:'Nuit',     bg:'#0B2D5B', ink:'#F7F4ED', muted:'#93A1B8', accent:'#FF9E5C', line:'#1E4272', card:'#12355F', sable:'#C9B489', deep:'#082444' },
  };

  /* Les robes officielles et leur teinte. Table FERMÉE : la valeur finit dans
     un attribut fill, une saisie client recopiée telle quelle permettrait
     d'injecter du SVG côté serveur. */
  const ROBES = {
    bai:      { nom: 'Bai',      hex: '#6B4423' },
    alezan:   { nom: 'Alezan',   hex: '#B5651D' },
    gris:     { nom: 'Gris',     hex: '#B8B5AE' },
    noir:     { nom: 'Noir',     hex: '#26221F' },
    palomino: { nom: 'Palomino', hex: '#D9B26A' },
    isabelle: { nom: 'Isabelle', hex: '#C8A87C' },
  };
  const MARQUES = { aucune: 'Aucune', etoile: 'Étoile', liste: 'Liste', 'belle-face': 'Belle face' };

  const esc = s => String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const up = s => String(s == null ? '' : s).toUpperCase();
  const num = s => { const m = String(s == null ? '' : s).replace(',', '.').match(/-?\d+(\.\d+)?/); return m ? parseFloat(m[0]) : null; };
  const fit = (t, maxW, maxS, cwf, ls, minS) => { const n = String(t).length || 1; minS = minS || 12;
    for (let s = maxS; s > minS; s -= 1) if (n * (cwf * s + ls) <= maxW) return s; return minS; };
  const T = (x,y,size,fill,txt,o) => { o = o || {};
    return `<text x="${x}" y="${y}" font-family="${o.f||'Sora'}" font-size="${size}" font-weight="${o.w||600}" fill="${fill}"`
      + (o.ls?` letter-spacing="${o.ls}"`:'') + ` text-anchor="${o.a||'middle'}"` + (o.op?` opacity="${o.op}"`:'') + `>${txt}</text>`; };

  function stats(p, data, y) {
    const it = [];
    for (let i = 1; i <= 3; i++) {
      const raw = String(data['s' + i] || '').trim(); if (!raw) continue;
      const [v, l] = raw.split('|').map(s => (s || '').trim());
      if (v) it.push([v, l || '']);
    }
    if (!it.length) return '';
    const x0 = 54, w = (PW - 2*x0) / it.length;
    let s = `<line x1="${x0}" y1="${y-34}" x2="${PW-x0}" y2="${y-34}" stroke="${p.line}" stroke-width="1.5"/>`;
    it.forEach((c, i) => {
      const cc = x0 + w*i + w/2;
      if (i) s += `<line x1="${(x0+w*i).toFixed(0)}" y1="${y-14}" x2="${(x0+w*i).toFixed(0)}" y2="${y+34}" stroke="${p.line}" stroke-width="1.5"/>`;
      s += T(cc, y+6, fit(c[0], w-24, 38, 0.56, 0, 17), p.ink, esc(c[0]), { f:'Oswald', w:700 })
        + T(cc, y+30, 12, p.muted, esc(up(c[1])), { w:600, ls:2.5 });
    });
    return s;
  }

  /** Tête vue de face, schématique — c'est le dessin du signalement qui figure
      sur le passeport, pas un portrait. Front LARGE, chanfrein qui se resserre
      puis se rouvre au bout du nez : sans ce ré-élargissement la tête lit
      « museau pointu » et non « cheval ». */
  function tete(cx, cy, h, p, marque, blanc) {
    const w = h * 0.60, t = y => cy - h/2 + h*y;
    let g = '';
    /* oreilles, proches et peu acérées */
    for (const s of [-1, 1]) g += `<path d="M${(cx + s*w*0.22).toFixed(1)} ${t(0.06).toFixed(1)}
      Q${(cx + s*w*0.34).toFixed(1)} ${t(-0.16).toFixed(1)} ${(cx + s*w*0.38).toFixed(1)} ${t(-0.10).toFixed(1)}
      Q${(cx + s*w*0.40).toFixed(1)} ${t(0.06).toFixed(1)} ${(cx + s*w*0.34).toFixed(1)} ${t(0.14).toFixed(1)} Z" fill="${p.ink}" opacity="0.88"/>`;
    /* crâne large → chanfrein resserré → bout du nez rouvert et arrondi */
    g += `<path d="M${(cx - w*0.50).toFixed(1)} ${t(0.16).toFixed(1)}
      Q${cx} ${t(-0.02).toFixed(1)} ${(cx + w*0.50).toFixed(1)} ${t(0.16).toFixed(1)}
      Q${(cx + w*0.47).toFixed(1)} ${t(0.40).toFixed(1)} ${(cx + w*0.30).toFixed(1)} ${t(0.56).toFixed(1)}
      Q${(cx + w*0.25).toFixed(1)} ${t(0.72).toFixed(1)} ${(cx + w*0.33).toFixed(1)} ${t(0.86).toFixed(1)}
      Q${(cx + w*0.30).toFixed(1)} ${t(1.00).toFixed(1)} ${cx} ${t(1.00).toFixed(1)}
      Q${(cx - w*0.30).toFixed(1)} ${t(1.00).toFixed(1)} ${(cx - w*0.33).toFixed(1)} ${t(0.86).toFixed(1)}
      Q${(cx - w*0.25).toFixed(1)} ${t(0.72).toFixed(1)} ${(cx - w*0.30).toFixed(1)} ${t(0.56).toFixed(1)}
      Q${(cx - w*0.47).toFixed(1)} ${t(0.40).toFixed(1)} ${(cx - w*0.50).toFixed(1)} ${t(0.16).toFixed(1)} Z" fill="${p.ink}" opacity="0.90"/>`;
    /* marque blanche en tête */
    if (marque === 'etoile') g += `<ellipse cx="${cx}" cy="${t(0.28).toFixed(1)}" rx="${(w*0.11).toFixed(1)}" ry="${(w*0.13).toFixed(1)}" fill="${blanc}"/>`;
    if (marque === 'liste') g += `<path d="M${(cx-w*0.075).toFixed(1)} ${t(0.20).toFixed(1)} L${(cx+w*0.075).toFixed(1)} ${t(0.20).toFixed(1)}
      L${(cx+w*0.055).toFixed(1)} ${t(0.82).toFixed(1)} L${(cx-w*0.055).toFixed(1)} ${t(0.82).toFixed(1)} Z" fill="${blanc}"/>`;
    if (marque === 'belle-face') g += `<path d="M${(cx-w*0.30).toFixed(1)} ${t(0.20).toFixed(1)}
      Q${cx} ${t(0.10).toFixed(1)} ${(cx+w*0.30).toFixed(1)} ${t(0.20).toFixed(1)}
      L${(cx+w*0.31).toFixed(1)} ${t(0.90).toFixed(1)} Q${cx} ${t(0.98).toFixed(1)} ${(cx-w*0.31).toFixed(1)} ${t(0.90).toFixed(1)} Z" fill="${blanc}" opacity="0.95"/>`;
    /* yeux et naseaux, en creux dans la robe */
    for (const s of [-1, 1]) g += `<ellipse cx="${(cx + s*w*0.31).toFixed(1)}" cy="${t(0.30).toFixed(1)}" rx="${(w*0.055).toFixed(1)}" ry="${(w*0.070).toFixed(1)}" fill="${blanc}" opacity="0.70"/>`;
    for (const s of [-1, 1]) g += `<ellipse cx="${(cx + s*w*0.11).toFixed(1)}" cy="${t(0.88).toFixed(1)}" rx="${(w*0.045).toFixed(1)}" ry="${(w*0.060).toFixed(1)}" fill="${blanc}" opacity="0.42"/>`;
    return g;
  }

  /** Un membre et sa balzane. haut = hauteur du blanc, en pourcentage. */
  function membre(cx, cy, h, p, haut, nom, blanc) {
    const w = h * 0.19, f = Math.max(0, Math.min(100, haut)) / 100;
    let g = `<rect x="${(cx-w/2).toFixed(1)}" y="${(cy-h/2).toFixed(1)}" width="${w.toFixed(1)}" height="${h.toFixed(1)}" rx="${(w/2).toFixed(1)}" fill="${p.ink}" opacity="0.90"/>`;
    if (f > 0) {
      const hb = h * f * 0.72;
      g += `<rect x="${(cx-w/2).toFixed(1)}" y="${(cy + h/2 - hb).toFixed(1)}" width="${w.toFixed(1)}" height="${hb.toFixed(1)}" rx="${(w/2).toFixed(1)}" fill="${blanc}"/>`
        + `<rect x="${(cx-w/2).toFixed(1)}" y="${(cy + h/2 - hb).toFixed(1)}" width="${w.toFixed(1)}" height="${hb.toFixed(1)}" rx="${(w/2).toFixed(1)}" fill="none" stroke="${p.ink}" stroke-width="1.6" opacity="0.45"/>`;
    }
    g += `<rect x="${(cx-w*0.64).toFixed(1)}" y="${(cy+h/2-3).toFixed(1)}" width="${(w*1.28).toFixed(1)}" height="9" rx="3" fill="${p.ink}" opacity="0.55"/>`;
    return g + T(cx, cy + h/2 + 36, 12, p.muted, esc(up(nom)), { w:600, ls:1.5 });
  }

  function render(data, opts) {
    data = data || {}; opts = opts || {};
    const p = PALETTES[opts.palette] || PALETTES.craie;
    const mode = String(data.mode || 'fiche').toLowerCase();
    const cx = PW / 2;
    const blanc = p.bg;

    /* ---- mode « nom » : le nom occupe la page, pas d'en-tête classique ---- */
    if (mode === 'nom') {
      const nom = up(data.nom) || up(data.titre) || 'SON NOM';
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${PW} ${PH}" font-family="'Sora','Inter',sans-serif">
<rect width="${PW}" height="${PH}" fill="${p.bg}"/>
${data.kicker ? T(cx, 300, 15, p.accent, esc(up(data.kicker)), { w:700, ls:6 }) : ''}
<line x1="${MX}" y1="340" x2="${PW-MX}" y2="340" stroke="${p.line}" stroke-width="2"/>
${T(cx, 470, fit(nom, 680, 128, 0.52, 2, 40), p.ink, esc(nom), { f:'Oswald', w:700, ls:1 })}
${data.surnom ? T(cx, 540, 22, p.muted, esc(up(data.surnom)), { w:600, ls:5 }) : ''}
<line x1="${MX}" y1="600" x2="${PW-MX}" y2="600" stroke="${p.line}" stroke-width="2"/>
${data.ligne1 ? T(cx, 668, fit(up(data.ligne1), 660, 17, 0.66, 3, 11), p.ink, esc(up(data.ligne1)), { w:700, ls:3 }) : ''}
${data.ligne2 ? T(cx, 706, fit(up(data.ligne2), 660, 15, 0.66, 3, 10), p.muted, esc(up(data.ligne2)), { w:600, ls:3 }) : ''}
${stats(p, data, 940)}
</svg>`;
    }

    const titre = up(data.titre) || 'MON CHEVAL';
    const ts = fit(titre, 690, 74, 0.54, 2, 34);
    const kicker = up(data.kicker) || 'SON IDENTITÉ';
    const sous = up(data.sous) || '';
    let corps = '', barreY = 950;

    if (mode === 'caractere') {
      const traits = [];
      for (let i = 1; i <= 6; i++) {
        const raw = String(data['t' + i] || '').trim(); if (!raw) continue;
        const [g, d, v] = raw.split('|').map(s => (s || '').trim());
        const pc = num(v);
        if (g && d && pc != null) traits.push([g, d, Math.max(0, Math.min(100, pc)) / 100]);
      }
      if (traits.length) {
        const zone = 560, pas = Math.min(96, zone / traits.length), y0 = 372 + (zone - pas*traits.length) / 2;
        traits.forEach((t, i) => {
          const y = y0 + i * pas;
          /* La piste se raccourcit quand un libellé est long : sinon un mot
             comme « INDÉPENDANT » vient mordre sur le curseur. */
          const lg = Math.max(t[0].length, t[1].length);
          const marge = Math.max(150, 62 + lg * 9.4);
          const x0 = MX + marge, x1 = PW - MX - marge;
          corps += T(MX, y + 5, 15, p.muted, esc(up(t[0])), { w:600, ls:1.5, a:'start' })
            + T(PW - MX, y + 5, 15, p.muted, esc(up(t[1])), { w:600, ls:1.5, a:'end' })
            + `<line x1="${x0.toFixed(1)}" y1="${y}" x2="${x1.toFixed(1)}" y2="${y}" stroke="${p.line}" stroke-width="3" stroke-linecap="round"/>`;
          for (let k = 0; k <= 4; k++) corps += `<circle cx="${(x0 + (x1-x0)*k/4).toFixed(1)}" cy="${y}" r="3" fill="${p.line}"/>`;
          const ccx = x0 + (x1 - x0) * t[2];
          corps += `<circle cx="${ccx.toFixed(1)}" cy="${y}" r="13" fill="${p.accent}"/><circle cx="${ccx.toFixed(1)}" cy="${y}" r="4.5" fill="${p.bg}"/>`;
        });
      } else corps = T(cx, 600, 21, p.muted, 'SAISISSEZ AU MOINS UN TRAIT DE CARACTÈRE', { w:600, ls:2 });
      if (data.resume) corps += T(cx, 962, fit(up(data.resume), 660, 15, 0.66, 4, 11), p.accent, esc(up(data.resume)), { w:700, ls:4 });
      barreY = 0;

    } else if (mode === 'signalement') {
      const marque = MARQUES[String(data.marqueTete || 'aucune').toLowerCase()] ? String(data.marqueTete).toLowerCase() : 'aucune';
      corps = tete(cx, 480, 300, p, marque, blanc);
      const mem = [['ag', 'Ant. gauche'], ['ad', 'Ant. droit'], ['pg', 'Post. gauche'], ['pd', 'Post. droit']];
      mem.forEach((m, i) => { corps += membre(148 + i * 168, 772, 150, p, num(data[m[0]]) || 0, m[1], blanc); });
      barreY = 960;

    } else if (mode === 'robe') {
      const choisie = ROBES[String(data.robe || 'bai').toLowerCase()] ? String(data.robe).toLowerCase() : 'bai';
      const cles = Object.keys(ROBES);
      cles.forEach((k, i) => {
        const col = i % 3, lig = Math.floor(i / 3);
        const x = 96 + col * 216, y = 372 + lig * 250, actif = k === choisie;
        corps += `<rect x="${x}" y="${y}" width="176" height="176" rx="14" fill="${ROBES[k].hex}"/>`
          + (actif ? `<rect x="${x-7}" y="${y-7}" width="190" height="190" rx="18" fill="none" stroke="${p.accent}" stroke-width="3"/>` : '')
          + T(x + 88, y + 210, 17, actif ? p.ink : p.muted, up(ROBES[k].nom), { f:'Oswald', w:700, ls:1 });
      });
      barreY = 960;

    } else { /* fiche */
      const lignes = [];
      for (let i = 1; i <= 8; i++) {
        const raw = String(data['l' + i] || '').trim(); if (!raw) continue;
        const [lab, v] = raw.split('|').map(s => (s || '').trim());
        if (lab && v) lignes.push([lab, v]);
      }
      if (lignes.length) {
        const zone = 560, pas = Math.min(90, zone / lignes.length), y0 = 380 + (zone - pas*lignes.length) / 2;
        lignes.forEach((l, i) => {
          const y = y0 + i * pas;
          corps += T(MX, y, 16, p.muted, esc(up(l[0])), { w:600, ls:2, a:'start' })
            + T(PW - MX, y + 8, fit(l[1], 430, 34, 0.58, 0, 18), p.ink, esc(l[1]), { f:'Oswald', w:700, a:'end' });
          if (i < lignes.length - 1) corps += `<line x1="${MX}" y1="${(y + pas/2 - 12).toFixed(1)}" x2="${PW-MX}" y2="${(y + pas/2 - 12).toFixed(1)}" stroke="${p.line}" stroke-width="1.5"/>`;
        });
      } else corps = T(cx, 600, 21, p.muted, 'SAISISSEZ AU MOINS UNE LIGNE, AU FORMAT RACE SELLE FRANÇAIS', { w:600, ls:1.5 });
      if (data.depuis) corps += T(cx, 300, 15, p.accent, esc(up(data.depuis)), { w:700, ls:4 });
      barreY = 986;
    }

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${PW} ${PH}" font-family="'Sora','Inter',sans-serif">
<rect width="${PW}" height="${PH}" fill="${p.bg}"/>
${T(cx, 84, 19, p.accent, esc(kicker), { w:700, ls:5 })}
${T(cx, 168, ts, p.ink, esc(titre), { f:'Oswald', w:700, ls:2 })}
${sous ? T(cx, 204, fit(sous, 660, 17, 0.66, 3.5, 11), p.muted, esc(sous), { w:600, ls:3.5 }) : ''}
${corps}
${barreY ? stats(p, data, barreY) : ''}
${data.cavalier ? T(cx, 1024, 19, p.ink, esc(up(data.cavalier)), { f:'Oswald', w:700, ls:2 }) : ''}
</svg>`;
  }

  window.SNEqCheval = { render, PALETTES, ROBES, MARQUES };
})();
